import { Box, Stack } from "@mui/material";
import { createContext, useContext, type FC } from "react";

import { FootnoteInput } from "./FootnoteInput";
import { FONT_SIZE } from "./constants";

import type { ICellRendererParams } from "ag-grid-community";

/**
 * An SBS page's "illustrated from" entry: the source of the enrollment or
 * volume its rates are illustrated with, which the marketing presentation
 * prints in place of the "_" in the tab's "... illustrated from _" row.
 */
export interface IllustratedFromEntry {
  /** The wording before the entry on the page's tab, e.g. "Enrollment illustrated from". */
  label: string;
  /** The saved entry, or "" when there is none. */
  value: string;
  onChange: (illustratedFrom: string) => void;
}

/**
 * Supplies the entry to the Rates row's label cell through context rather than
 * through the column definition: RatesGrid keeps its columnDefs referentially
 * stable because a new array remeasures every autoHeight cell, which reads as
 * the grid jumping, and saving an entry must not do that.
 */
export const IllustratedFromContext = createContext<
  IllustratedFromEntry | undefined
>(undefined);

/**
 * The Rates row's pinned label cell. On a page with an entry, the entry's
 * editable input sits under the "Rates" label, beside the enrollment it
 * describes, where the marketing presentation prints it at the head of the
 * rate section.
 */
export const RatesLabelCell: FC<
  ICellRendererParams<Record<string, string>, string>
> = ({ value }) => {
  const illustratedFrom = useContext(IllustratedFromContext);

  if (!illustratedFrom) return value ?? null;

  return (
    <Stack spacing={1} sx={{ py: 1, pr: 1, whiteSpace: "normal" }}>
      <span>{value}</span>
      <Box
        sx={{
          lineHeight: 1.4,
          "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
          "&:focus-within": { backgroundColor: "rgba(25, 118, 210, 0.08)" },
          "& textarea": { paddingLeft: 0 },
        }}
      >
        <Box component="span" sx={{ fontSize: FONT_SIZE }}>
          {illustratedFrom.label}
        </Box>
        <FootnoteInput
          serverValue={illustratedFrom.value}
          placeholder="Add source"
          ariaLabel={illustratedFrom.label}
          commitOnEnter
          onCommit={illustratedFrom.onChange}
        />
      </Box>
    </Stack>
  );
};
